import Tooltip from "@/v2-ui/tooltip"
import Dot, { type DotProps } from "@/v2-backoffice-client/components/dot"
import { CrmCheckingDot } from "@/v2-backoffice-client/crm/CrmStatusLine"
import type { ContactEmailChecks } from "@/v2-backoffice-client/crm/crm.contactChecks.hooks"
import { normalizeEmail } from "@/v2-backoffice-client/crm/crm.utils"

export type ContactEmailStatusDotProps = {
  email: string
  checks: ContactEmailChecks
  /** The form's "create missing contacts" choice — words the "new" state. */
  createContact: boolean
}

/**
 * The bulk tables' per-row verdict as a single dot INSIDE the email cell
 * (absolutely positioned at its right edge — no column, no width taken):
 * red = already an app user, green = HubSpot contact (will be linked),
 * blue = new to both (created, or not, per the checkbox). The label and the
 * who/where live in the tooltip.
 */
function ContactEmailStatusDot(props: ContactEmailStatusDotProps) {
  const { email, checks, createContact } = props

  const normalized = normalizeEmail(email)
  if (!checks.checkedEmails.has(normalized)) return null

  const slot = "pointer-events-auto absolute right-2.5 top-1/2 -translate-y-1/2"

  if (!checks.answered) {
    return (
      <span className={slot} aria-label="Checking">
        <CrmCheckingDot />
      </span>
    )
  }

  const check = checks.byEmail.get(normalized)
  let variant: DotProps["variant"]
  let label: string
  let description: string

  if (check?.existingUser) {
    const user = check.existingUser
    const name =
      [user.FirstName, user.LastName].filter(Boolean).join(" ") || "A user"
    variant = "destructive"
    label = user.IsDeletedUser ? "Removed user" : "Already a user"
    description = `${name}${user.CustomerName ? ` (${user.CustomerName})` : ""} already has this email in Backoffice${user.IsDeletedUser ? " — the account was removed" : ""}.`
  } else if (check?.contact) {
    const contact = check.contact
    const name =
      [contact.FirstName, contact.LastName].filter(Boolean).join(" ") ||
      "the existing contact"
    variant = "success-1"
    label = "In HubSpot"
    description = `Will be linked to ${name}${contact.OwnerName ? ` (owner ${contact.OwnerName})` : ""} in HubSpot.`
  } else {
    variant = createContact ? "info" : "muted"
    label = createContact ? "New — will be created in HubSpot" : "New"
    description = checks.hubspotSearched
      ? createContact
        ? "No HubSpot contact has this email yet — one will be created."
        : "No HubSpot contact has this email — none will be created (see the checkbox below)."
      : "Not in Backoffice. HubSpot could not be checked right now."
  }

  return (
    <Tooltip>
      <Tooltip.Trigger asChild>
        <span className={slot} aria-label={label}>
          <Dot variant={variant} />
        </span>
      </Tooltip.Trigger>
      {/* The tooltip body is a flex row; stack the two lines explicitly. */}
      <Tooltip.Content side="top">
        <span className="flex max-w-xs flex-col gap-0.5">
          <span className="font-medium">{label}</span>
          <span>{description}</span>
        </span>
      </Tooltip.Content>
    </Tooltip>
  )
}

export default ContactEmailStatusDot
